import useActivityTracker from '@frontend/client/contexts/useActivityTracker';
import { getRouterAddress } from '@frontend/client/hooks';
import { useAllowanceToken } from '@frontend/client/hooks/tokenIn';
import { CUSTOM_ABI } from '@frontend/common';
import { TokenValue } from '@frontend/types';
import { Button, Flex, Text } from '@radix-ui/themes';
import { useMutation } from '@tanstack/react-query';
import { getChain } from '@yodlpay/tokenlists';
import assert from 'assert';
import { useEffect, useMemo, useState } from 'react';
import { Address, erc20Abi, zeroAddress } from 'viem';
import {
  useChainId,
  useSimulateContract,
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';

interface AllowanceState {
  isLoading: boolean;
  refetch: () => void;
}

interface ApproveButtonProps {
  allowance: AllowanceState;
  approveAmount: TokenValue;
}

interface ContractConfig {
  address: Address;
  abi: typeof erc20Abi;
  functionName: 'approve';
  chainId: number;
  args: [Address, bigint];
}

/**
 * Custom hook to manage the approval transaction state.
 * Important: When users navigate away and return to the page, allowWrite may reset to undefined after page reload.
 *            To prevent this, we store the txHash in the state.
 */
const useApprovalTransaction = (chainId?: number) => {
  const [txHash, setTxHash] = useState<`0x${string}`>();
  const [needsReset, setNeedsReset] = useState(false);
  const allowWrite = useWriteContract();
  const allowanceWriteTx = useWaitForTransactionReceipt({
    hash: txHash || allowWrite.data,
    chainId,
  });

  useEffect(() => {
    if (allowWrite.data) {
      setTxHash(allowWrite.data);
      setNeedsReset(false);
    }
  }, [allowWrite.data]);

  return {
    txHash,
    allowWrite,
    allowanceWriteTx,
    isLoading: allowWrite.isPending || allowanceWriteTx.isLoading,
    error: allowanceWriteTx.error,
    needsReset,
    setNeedsReset,
  };
};

/**
 * The ApproveButton component is used to approve a token for the router.
 * It handles the approval transaction state and allows the user to approve the token.
 */
export const ApproveButton = ({
  allowance,
  approveAmount,
}: ApproveButtonProps) => {
  const currentChainId = useChainId();
  const {
    isPending: switchChainPending,
    switchChainAsync,
    error: switchChainError,
  } = useSwitchChain();
  const tracker = useActivityTracker((state) => state.trackEvent);

  const { token } = useAllowanceToken();

  const chainId = token?.chainId;

  const {
    allowanceWriteTx,
    allowWrite,
    isLoading,
    error: allowanceWriteTxError,
    needsReset,
    setNeedsReset,
  } = useApprovalTransaction(chainId);

  const contractParams: ContractConfig = useMemo(() => {
    assert(chainId, '[ApproveButton] Chain id is undefined');
    const tokenAbi = getTokenAbi(token?.symbol, chainId);
    const routerAddress = getRouterAddress(chainId);

    return {
      address: token?.address as Address,
      abi: tokenAbi,
      functionName: 'approve',
      chainId,
      args: [
        routerAddress || zeroAddress,
        needsReset ? 0n : approveAmount.value,
      ],
    };
  }, [token, chainId, approveAmount, needsReset]);

  const { error: prepareError, isLoading: prepareLoading } =
    useSimulateContract(contractParams);

  useEffect(() => {
    if (allowWrite.data) {
      const interval = setInterval(() => {
        allowance.refetch();
      }, 1000); // Refetch every 1 seconds

      return () => clearInterval(interval);
    }
  }, [allowWrite.data, allowance]);

  useEffect(() => {
    if (allowanceWriteTx.isSuccess) {
      allowance.refetch();
    }
  }, [allowanceWriteTx.isSuccess, allowance]);

  const approveMutation = useMutation({
    mutationFn: async () => {
      assert(chainId, '[ApproveButton] Chain id is undefined');
      try {
        if (chainId !== currentChainId) {
          await switchChainAsync({ chainId });
        }

        if (!allowWrite.writeContract) {
          throw new Error('Write contract not initialized');
        }

        return await allowWrite.writeContractAsync?.(contractParams);
      } catch (error: any) {
        // Check if this is a USDT-style revert that needs allowance reset
        if (
          error?.message?.includes('execution reverted') &&
          token?.symbol === 'USDT' &&
          !needsReset
        ) {
          setNeedsReset(true);
          throw new Error('USDT requires allowance reset. Please try again.');
        }
        throw error;
      }
    },
    onMutate: () => {
      tracker('Approving');
    },
    onSuccess: () => {
      if (needsReset) {
        // If this was a reset transaction, trigger another approval
        setTimeout(() => approveMutation.mutate(), 500);
      } else {
        allowance.refetch();
      }
    },
  });

  const error = useMemo(() => {
    // For prepare errors only, try setting needsReset as a potential fix
    if (prepareError && !needsReset) {
      setNeedsReset(true);
      return '[Prepare Error] Transaction simulation failed. Attempting to reset approval...';
    }

    if (
      approveMutation.error?.message
        .toLowerCase()
        .includes('user rejected the request')
    ) {
      setNeedsReset(false);
      return null;
    }

    // Get the most relevant error
    const activeError =
      approveMutation.error ||
      prepareError ||
      allowanceWriteTxError ||
      switchChainError;

    if (!activeError) return null;

    // Extract detailed error information
    const errorDetails = {
      message: activeError.message,
      code: (activeError as any).code,
      details: (activeError as any).details || '',
      data: (activeError as any).data
        ? JSON.stringify((activeError as any).data)
        : '',
    };

    // Format the error message with source
    const errorSource = (() => {
      switch (activeError) {
        case approveMutation.error:
          return 'Approval Error';
        case prepareError:
          return 'Prepare Error';
        case allowanceWriteTxError:
          return 'Transaction Error';
        default:
          return 'Chain Switch Error';
      }
    })();

    // Format the error message
    return `[${errorSource}] ${errorDetails.message}${
      errorDetails.code ? `\nCode: ${errorDetails.code}` : ''
    }${errorDetails.details ? `\nDetails: ${errorDetails.details}` : ''}${
      errorDetails.data ? `\nData: ${errorDetails.data}` : ''
    }`;
  }, [
    approveMutation.error,
    prepareError,
    allowanceWriteTxError,
    switchChainError,
    setNeedsReset,
    needsReset,
  ]);

  const buttonText = useMemo(() => {
    assert(chainId, '[ApproveButton] Chain id is undefined');

    if (isLoading) {
      return needsReset ? 'Resetting Approval...' : 'Approving';
    }

    if (switchChainPending) {
      return `Switching to ${getChain(chainId)?.chainName}...`;
    }

    return needsReset ? 'Reset Approval' : 'Approve';
  }, [isLoading, switchChainPending, needsReset, chainId]);

  return (
    <Flex direction="column" gap="2" className="w-full">
      {error && (
        <Text
          size="2"
          weight="bold"
          color="red"
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          onClick={() => {
            navigator.clipboard.writeText(error);
          }}
        >
          {error}
        </Text>
      )}

      <Button
        data-testid="approve"
        disabled={
          isLoading ||
          (!!error && !needsReset) ||
          switchChainPending ||
          prepareLoading
        }
        onClick={() => approveMutation.mutate()}
        style={{ width: '100%' }}
        size="4"
        loading={isLoading || prepareLoading}
      >
        {buttonText}
      </Button>
    </Flex>
  );
};

export const getTokenAbi = (
  symbol: string,
  chainId: number,
): typeof erc20Abi => {
  if (symbol in CUSTOM_ABI && chainId in CUSTOM_ABI[symbol]) {
    return CUSTOM_ABI[symbol][chainId];
  }
  return erc20Abi;
};
